const REFRESH_DELAY = 600
const GUESTS_PLACEHOLDER = "%{guests}"
const PARTY_FIELDS = [
  "adults_count",
  "children_count",
  "teens_count",
  "infants_count",
]

stimulusApp.register(
  "cruise-picker",
  class extends Stimulus.Controller {
    static targets = ["slot", "value", "guests", "voyage", "pane", "cabinClass"]
    static outlets = ["cruise-route-tabs"]
    static values = {
      pageUrl: String,
      guestParts: Object,
      introTemplate: String,
    }

    #timer

    disconnect() {
      clearTimeout(this.#timer)
    }

    toggle(event) {
      event.preventDefault()
      const slot = event.currentTarget.closest(
        "[data-cruise-picker-target='slot']",
      )
      const wasExpanded = slot.getAttribute("aria-expanded") === "true"

      this.closeAll()
      if (!wasExpanded) slot.setAttribute("aria-expanded", "true")
    }

    select(event) {
      this.#label(event.currentTarget)
      this.#refresh()
      this.dispatch("selectionChanged", {
        bubbles: true,
        detail: { title: event.currentTarget.dataset.cabinsTitle },
      })
      this.#closeThenAnnounce()
    }

    check() {
      if (!this.voyageTargets.some((radio) => radio.checked)) {
        this.choose(this.voyageTargets[0].value)
      }
      this.#closeThenAnnounce()
    }

    selectCabin() {
      if (!this.hasCruiseRouteTabsOutlet) return

      const tabs = this.cruiseRouteTabsOutlet
      const selected = tabs.buttonTargets.find((button) =>
        button.classList.contains(tabs.selectedClass),
      )
      if (!selected) return

      this.cabinClassTarget.value = selected.dataset.cruiseRouteTabsIdParam
      this.cabinClassTarget.disabled = false
    }

    scheduleRefresh() {
      this.#previewParty()
      if (this.voyageTargets.some((radio) => radio.checked)) {
        document
          .getElementById(this.element.dataset.turboFrame)
          .setAttribute("aria-busy", "true")
      }
      clearTimeout(this.#timer)
      this.#timer = setTimeout(() => this.#refresh(), REFRESH_DELAY)
    }

    sync(event) {
      if (this.#timer) {
        if (event.target.id === this.element.dataset.turboFrame) {
          event.target.setAttribute("aria-busy", "true")
        }
        return
      }
      const summary = event.target.querySelector("[data-guests-summary]")
      if (!summary) return

      if (this.hasGuestsTarget) {
        this.guestsTarget.textContent = summary.dataset.guestsSummary
      }

      this.choose(summary.dataset.voyageId)
      this.#syncUrl()
    }

    choose(voyageId, cabinClass) {
      if (cabinClass) {
        this.cabinClassTarget.value = cabinClass
        this.cabinClassTarget.disabled = false
      }

      const picked = this.voyageTargets.find(
        (radio) => radio.value === voyageId,
      )
      if (!picked) return

      picked.checked = true
      this.#label(picked)
    }

    closeOnOutsideClick(event) {
      if (this.element.contains(event.target)) return

      this.closeAll()
    }

    closeAll() {
      this.slotTargets.forEach((slot) =>
        slot.setAttribute("aria-expanded", "false"),
      )
    }

    async #closeThenAnnounce() {
      const slot = this.slotTargets.find(
        (candidate) => candidate.getAttribute("aria-expanded") === "true",
      )
      const collapsing =
        slot && this.paneTargets.find((pane) => slot.contains(pane))

      this.closeAll()
      const animations = collapsing ? collapsing.getAnimations() : []
      if (animations.length) {
        await Promise.allSettled(
          animations.map((animation) => animation.finished),
        )
      }
      this.#announcePick()
    }

    #announcePick() {
      this.dispatch("dateSelected", { bubbles: true })
    }

    #refresh() {
      clearTimeout(this.#timer)
      this.#timer = undefined
      if (!this.voyageTargets.some((radio) => radio.checked)) return

      this.element.requestSubmit()
    }

    #previewParty() {
      const guests = this.guestPartsValue[this.#party()]
      if (!guests) return

      if (this.hasGuestsTarget) {
        this.guestsTarget.textContent = guests
      }

      this.dispatch("selectionChanged", {
        bubbles: true,
        detail: {
          intro: this.introTemplateValue.replace(GUESTS_PLACEHOLDER, guests),
        },
      })
    }

    #party() {
      const party = new FormData(this.element)

      return PARTY_FIELDS.map((field) => party.get(field)).join("-")
    }

    #label(radio) {
      const slot = radio.closest("[data-cruise-picker-target='slot']")
      const value = this.valueTargets.find((candidate) =>
        slot.contains(candidate),
      )

      if (value) value.textContent = radio.dataset.label
    }

    #syncUrl() {
      const url = new URL(this.pageUrlValue, window.location.origin)
      new FormData(this.element).forEach((value, key) =>
        url.searchParams.set(key, value),
      )
      history.replaceState(history.state, "", `${url.pathname}${url.search}`)
    }
  },
)
